import { apiClient } from "@/lib/apiClient";
import { BarChart3, ChevronDown, ExternalLink, Globe, LayoutDashboard, LifeBuoy, LogOut, Menu, ShieldCheck, User as UserIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useAuth } from "@/components/AuthContext";
import { BetaBanner } from "@/components/BetaBanner";
import { toastApiError } from "@/lib/apiErrors";
import { setLanguage } from "@/lib/i18n";

interface Props {
	slug?: string;
	universeId?: string | null;
	onBurgerClick?: () => void;
}

const logoSrc = `${import.meta.env.BASE_URL}logo-simplified.png?v=20260523`;

function getInitials(email: string): string {
	const local = email.split("@")[0] ?? "";
	const parts = local.split(/[._-]/).filter(Boolean);
	if (parts.length >= 2 && parts[0] && parts[1]) {
		return (parts[0][0] + parts[1][0]).toUpperCase();
	}
	return local.slice(0, 2).toUpperCase();
}

export function AppHeader({ slug, universeId, onBurgerClick }: Props) {
	const { t, i18n } = useTranslation();
	const [menuOpen, setMenuOpen] = useState(false);
	const { user, status, logout } = useAuth();
	const navigate = useNavigate();
	const menuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
				setMenuOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	async function handleLogout() {
		const didLogout = await logout();
		if (didLogout) {
			void navigate("/login", { replace: true });
		}
	}

	async function handleLanguageSwitch() {
		const newLang = i18n.language === "fr" ? "en" : ("fr" as const);
		if (status === "authenticated") {
			try {
				await apiClient.put("/api/auth/language", { language: newLang });
			} catch (err) {
				toastApiError(err, t("operationErrors.updateLanguage"));
				return;
			}
		}
		setLanguage(newLang);
	}

	const initials = user ? getInitials(user.email) : "";
	const sitePrefix = i18n.language === "fr" ? "/fr" : "";

	return (
		<>
			<BetaBanner />
		<header
			className="h-12 shrink-0 flex items-center justify-between px-5"
			style={{
				background: "rgba(10, 10, 11, 0.85)",
				backdropFilter: "blur(20px)",
				WebkitBackdropFilter: "blur(20px)",
				borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
			}}
		>
			<div className="flex items-center gap-3 md:gap-5">
				{onBurgerClick && (
					<button
						type="button"
						onClick={onBurgerClick}
						className="md:hidden flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
						style={{ color: "#71717a" }}
						aria-label="Open navigation"
						data-demo-navigation-toggle
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLButtonElement).style.color = "#e4e4e7";
							(e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.04)";
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLButtonElement).style.color = "#71717a";
							(e.currentTarget as HTMLButtonElement).style.background = "transparent";
						}}
					>
						<Menu size={18} />
					</button>
				)}

				<Link to="/dashboard" className="flex items-center gap-2.5">
					<img
						src={logoSrc}
						alt="Kitsulab"
						width={24}
						height={24}
						style={{ borderRadius: 5 }}
					/>
					<span
						className="hidden sm:inline text-sm font-bold"
						style={{ color: "#e4e4e7", letterSpacing: "-0.02em" }}
					>
						Kitsulab
					</span>
				</Link>

				{user?.plan && user.plan !== "free" && (
					<span
						className="hidden sm:inline text-[10px] font-bold px-2 py-0.5 rounded-full"
						style={{
							background:
								"linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)",
							color: "#fff",
							letterSpacing: "0.04em",
							textTransform: "uppercase",
						}}
					>
						{user.plan === "founder" ? "Founder" : t("nav.subscriber")}
					</span>
				)}

				{slug && (
					<span
						className="hidden md:inline font-mono text-[10px] px-2 py-0.5 rounded"
						style={{
							background: "rgba(255, 255, 255, 0.03)",
							border: "1px solid rgba(255, 255, 255, 0.06)",
							color: "#71717a",
						}}
					>
						{slug}
					</span>
				)}
				{universeId && (
					<a
						href={`https://create.roblox.com/dashboard/creations/experiences/${universeId}/overview`}
						target="_blank"
						rel="noopener noreferrer"
						className="hidden md:flex items-center gap-1 text-[11px] font-medium transition-colors"
						style={{ color: "#52525b" }}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#e4e4e7";
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#52525b";
						}}
					>
						Roblox
						<ExternalLink size={10} />
					</a>
				)}
			</div>

			<div className="flex items-center gap-3 md:gap-5">
				<div className="hidden md:flex items-center gap-5">
					<Link
						to="/dashboard"
						className="text-[13px] font-medium transition-colors"
						style={{ color: "#71717a" }}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#e4e4e7";
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#71717a";
						}}
					>
						{t("nav.myProjects")}
					</Link>
					<a
						href={`${sitePrefix}/docs/`}
						className="text-[13px] font-medium transition-colors"
						style={{ color: "#71717a" }}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#e4e4e7";
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#71717a";
						}}
					>
						{t("nav.docs")}
					</a>
					<a
						href={`${sitePrefix}/tutorials/`}
						className="text-[13px] font-medium transition-colors"
						style={{ color: "#71717a" }}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#e4e4e7";
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#71717a";
						}}
					>
						{t("nav.tutorials")}
					</a>
					<Link
						to="/pricing"
						className="text-[13px] font-medium transition-colors"
						style={{ color: "#71717a" }}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#e4e4e7";
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#71717a";
						}}
					>
						{t("nav.pricing")}
					</Link>
					<Link
						to="/support"
						className="text-[13px] font-medium transition-colors"
						style={{ color: "#71717a" }}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#e4e4e7";
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#71717a";
						}}
					>
						{t("nav.support")}
					</Link>
					{user?.canAccessAdminDashboard && (
						<>
							<Link
								to="/admin-dashboard"
								className="text-[13px] font-medium transition-colors"
								style={{ color: "#71717a" }}
								onMouseEnter={(e) => {
									(e.currentTarget as HTMLAnchorElement).style.color = "#e4e4e7";
								}}
								onMouseLeave={(e) => {
									(e.currentTarget as HTMLAnchorElement).style.color = "#71717a";
								}}
							>
								{t("nav.pilotage")}
							</Link>
							<Link
								to="/admin-support"
								className="text-[13px] font-medium transition-colors"
								style={{ color: "#71717a" }}
								onMouseEnter={(e) => {
									(e.currentTarget as HTMLAnchorElement).style.color = "#e4e4e7";
								}}
								onMouseLeave={(e) => {
									(e.currentTarget as HTMLAnchorElement).style.color = "#71717a";
								}}
							>
								{t("nav.supportAdmin")}
							</Link>
						</>
					)}
					<a
						href={`${sitePrefix}/contact/`}
						className="text-[13px] font-medium transition-colors"
						style={{ color: "#71717a" }}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#e4e4e7";
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLAnchorElement).style.color = "#71717a";
						}}
					>
						Contact
					</a>
				</div>

				<button
					type="button"
					onClick={() => { void handleLanguageSwitch(); }}
					className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all duration-200"
					style={{
						color: "#71717a",
						border: "1px solid rgba(255, 255, 255, 0.06)",
					}}
					onMouseEnter={(e) => {
						(e.currentTarget as HTMLButtonElement).style.borderColor =
							"rgba(255, 255, 255, 0.12)";
						(e.currentTarget as HTMLButtonElement).style.color = "#e4e4e7";
					}}
					onMouseLeave={(e) => {
						(e.currentTarget as HTMLButtonElement).style.borderColor =
							"rgba(255, 255, 255, 0.06)";
						(e.currentTarget as HTMLButtonElement).style.color = "#71717a";
					}}
				>
					<Globe size={12} />
					{i18n.language === "fr" ? "EN" : "FR"}
				</button>

				{}
				<div className="relative" ref={menuRef}>
					<button
						type="button"
						onClick={() => setMenuOpen((v) => !v)}
						className="flex items-center gap-2 transition-colors"
					>
						<div
							className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold"
							style={{
								background:
									"linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)",
								color: "#ffffff",
							}}
						>
							{initials}
						</div>
						<ChevronDown
							size={12}
							style={{
								color: "#52525b",
								transform: menuOpen ? "rotate(180deg)" : "none",
								transition: "transform 0.2s",
							}}
						/>
					</button>

					{menuOpen && (
						<div
							className="absolute right-0 top-full mt-2 w-48 rounded-lg py-1 z-50"
							style={{
								background: "#141415",
								border: "1px solid rgba(255, 255, 255, 0.06)",
								boxShadow:
									"0 8px 40px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.25)",
							}}
						>
							{user && (
								<div
									className="px-3 py-2 text-[11px] font-mono truncate"
									style={{
										color: "#52525b",
										borderBottom:
											"1px solid rgba(255, 255, 255, 0.06)",
									}}
								>
									{user.email}
								</div>
							)}
							<Link
								to="/account"
								onClick={() => setMenuOpen(false)}
								className="flex items-center gap-2.5 px-3 py-2 text-[13px] transition-colors w-full"
								style={{ color: "#71717a" }}
								onMouseEnter={(e) => {
									(e.currentTarget as HTMLAnchorElement).style.background =
										"rgba(255, 255, 255, 0.03)";
									(e.currentTarget as HTMLAnchorElement).style.color =
										"#e4e4e7";
								}}
								onMouseLeave={(e) => {
									(e.currentTarget as HTMLAnchorElement).style.background =
										"transparent";
									(e.currentTarget as HTMLAnchorElement).style.color =
										"#71717a";
								}}
							>
								<UserIcon size={14} />
								{t("nav.account")}
							</Link>
							<Link
								to="/dashboard"
								onClick={() => setMenuOpen(false)}
								className="flex items-center gap-2.5 px-3 py-2 text-[13px] transition-colors w-full"
								style={{ color: "#71717a" }}
								onMouseEnter={(e) => {
									(e.currentTarget as HTMLAnchorElement).style.background =
										"rgba(255, 255, 255, 0.03)";
									(e.currentTarget as HTMLAnchorElement).style.color =
										"#e4e4e7";
								}}
								onMouseLeave={(e) => {
									(e.currentTarget as HTMLAnchorElement).style.background =
										"transparent";
									(e.currentTarget as HTMLAnchorElement).style.color =
										"#71717a";
								}}
							>
								<LayoutDashboard size={14} />
								{t("nav.myProjects")}
							</Link>
							<Link
								to="/support"
								onClick={() => setMenuOpen(false)}
								className="flex items-center gap-2.5 px-3 py-2 text-[13px] transition-colors w-full"
								style={{ color: "#71717a" }}
								onMouseEnter={(e) => {
									(e.currentTarget as HTMLAnchorElement).style.background =
										"rgba(255, 255, 255, 0.03)";
									(e.currentTarget as HTMLAnchorElement).style.color =
										"#e4e4e7";
								}}
								onMouseLeave={(e) => {
									(e.currentTarget as HTMLAnchorElement).style.background =
										"transparent";
									(e.currentTarget as HTMLAnchorElement).style.color =
										"#71717a";
								}}
							>
								<LifeBuoy size={14} />
								{t("nav.support")}
							</Link>
							{user?.canAccessAdminDashboard && (
								<>
									<Link
										to="/admin-dashboard"
										onClick={() => setMenuOpen(false)}
										className="flex items-center gap-2.5 px-3 py-2 text-[13px] transition-colors w-full"
										style={{ color: "#71717a" }}
										onMouseEnter={(e) => {
											(e.currentTarget as HTMLAnchorElement).style.background =
												"rgba(255, 255, 255, 0.03)";
											(e.currentTarget as HTMLAnchorElement).style.color =
												"#e4e4e7";
										}}
										onMouseLeave={(e) => {
											(e.currentTarget as HTMLAnchorElement).style.background =
												"transparent";
											(e.currentTarget as HTMLAnchorElement).style.color =
												"#71717a";
										}}
									>
										<BarChart3 size={14} />
										{t("nav.pilotage")}
									</Link>
									<Link
										to="/admin-support"
										onClick={() => setMenuOpen(false)}
										className="flex items-center gap-2.5 px-3 py-2 text-[13px] transition-colors w-full"
										style={{ color: "#71717a" }}
										onMouseEnter={(e) => {
											(e.currentTarget as HTMLAnchorElement).style.background =
												"rgba(255, 255, 255, 0.03)";
											(e.currentTarget as HTMLAnchorElement).style.color =
												"#e4e4e7";
										}}
										onMouseLeave={(e) => {
											(e.currentTarget as HTMLAnchorElement).style.background =
												"transparent";
											(e.currentTarget as HTMLAnchorElement).style.color =
												"#71717a";
										}}
									>
										<ShieldCheck size={14} />
										{t("nav.supportAdmin")}
									</Link>
								</>
							)}
							<button
								type="button"
								onClick={handleLogout}
								className="flex items-center gap-2.5 px-3 py-2 text-[13px] transition-colors w-full text-left"
								style={{ color: "#71717a" }}
								onMouseEnter={(e) => {
									(e.currentTarget as HTMLButtonElement).style.background =
										"rgba(255, 255, 255, 0.03)";
									(e.currentTarget as HTMLButtonElement).style.color =
										"#ffb4ab";
								}}
								onMouseLeave={(e) => {
									(e.currentTarget as HTMLButtonElement).style.background =
										"transparent";
									(e.currentTarget as HTMLButtonElement).style.color =
										"#71717a";
								}}
							>
								<LogOut size={14} />
								{t("nav.logout")}
							</button>
						</div>
					)}
				</div>
			</div>
		</header>
		</>
	);
}
